import type { ServiceResponse } from "~/api/axios.ts";
import { api, getAuthHeaders } from "~/api/axios.ts";
import { addSortAndFilterParams } from "~/utils";
import type { Patient } from "./patients";
import type {
  MessageResponse,
  PaginationParams,
  SearchParams,
  SortingParams,
} from "./types";

export const INSOLE_STATUS = {
  WORKING: "WORKING",
  BROKEN: "BROKEN",
} as const;

export type InsoleStatus = (typeof INSOLE_STATUS)[keyof typeof INSOLE_STATUS];

export const INSOLE_GENDER = {
  male: "MALE",
  female: "FEMALE",
} as const;

export type InsoleGender = (typeof INSOLE_GENDER)[keyof typeof INSOLE_GENDER];

export const INSOLE_ARCH_TYPE = {
  HIGH: "HIGH",
  NEUTRAL: "NEUTRAL",
  LOW: "LOW",
} as const;

export type InsoleArchType =
  (typeof INSOLE_ARCH_TYPE)[keyof typeof INSOLE_ARCH_TYPE];

export const INSOLE_SIZES = [
  40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125,
  130, 135, 140, 145, 150, 155, 160,
];

export interface Insole {
  id: number;
  serial_number: string;
  gender: InsoleGender;
  shoe_size_us: number;
  arch_type: InsoleArchType;
  status: InsoleStatus;
  patient: Patient;
  settings: {
    auto_record: string;
    synced: boolean;
    last_synced_at: string | null;
  };
}

export const getInsoles = async ({
  page,
  per_page,
  search,
  order_by,
  sort_direction,
}: PaginationParams & SearchParams & SortingParams) => {
  const params: Record<string, string | number> = {
    page,
    per_page,
  };

  const fullParams = addSortAndFilterParams(
    search,
    order_by,
    sort_direction,
    params,
  );

  const response = await api.get<ServiceResponse<Insole[]>>("/insoles", {
    headers: getAuthHeaders(),
    params: fullParams,
  });

  return response.data;
};

export const deleteInsole = async (id: number) => {
  const response = await api.delete<ServiceResponse<MessageResponse>>(
    `/insoles/${id}`,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

interface CreateInsoleParams {
  serialNumber: string;
  gender: InsoleGender;
  size: number;
  archType: InsoleArchType;
}

export const createInsole = async ({
  serialNumber,
  gender,
  size,
  archType,
}: CreateInsoleParams) => {
  const response = await api.post<ServiceResponse<MessageResponse>>(
    "/insoles",
    {
      insoles: [
        {
          serial_number: serialNumber,
          shoe_size_us: size,
          gender,
          arch_type: archType,
        },
      ],
    },
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const updateInsoleSettings = async (
  insoleId: number,
  autoRecord: string,
) => {
  const response = await api.patch<ServiceResponse<MessageResponse>>(
    `/insoles/settings`,
    { insole_id: insoleId, auto_record: autoRecord },
    { headers: getAuthHeaders() },
  );
  return response.data;
};
